import React from 'react';
import { ShieldCheck, Mail, MapPin, Eye } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-bgmain pb-16 md:pb-24 pt-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header Hero banner */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-primary text-white p-8 md:p-12 mb-8 shadow-warm grain">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full mb-3 border border-white/10">
              <ShieldCheck className="w-4 h-4 text-golden" />
              <span className="text-xs font-heading font-medium tracking-wide">LEGAL POLICY</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-white">Privacy Policy</h1>
            <p className="text-white/80 font-body text-xs mt-3 uppercase tracking-wider">Effective Date: July 18, 2026</p>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl border border-borderbeige shadow-card p-6 md:p-10 font-body text-textprimary leading-relaxed space-y-8 animate-fade-in">
          <p className="text-base text-textsecondary">
            Welcome to <strong>Pushpanjali.cloud</strong> ("Pushpanjali", "we", "our", or "us"). We operate a cloud kitchen delivering freshly prepared homemade food in selected areas of Noida.
          </p>

          <hr className="border-borderbeige" />

          {/* Section 1 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">1</span>
              Information We Collect
            </h2>
            <p className="text-textsecondary text-sm">When you place an order or interact with our website, we may collect:</p>
            <ul className="list-disc list-inside text-sm text-textsecondary pl-4 space-y-1.5 font-body">
              <li>Full Name</li>
              <li>Mobile Number</li>
              <li>Email Address</li>
              <li>Delivery Address</li>
              <li>Order Details</li>
              <li>Payment Information (processed securely through our payment partner)</li>
              <li>Device and browser information for website analytics</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">2</span>
              How We Use Your Information
            </h2>
            <p className="text-textsecondary text-sm">We use your information to:</p>
            <ul className="list-disc list-inside text-sm text-textsecondary pl-4 space-y-1.5">
              <li>Process and deliver your orders</li>
              <li>Contact you regarding your order status</li>
              <li>Provide customer support</li>
              <li>Improve our products, kitchen service, and website</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraud and misuse of our network</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">3</span>
              Food Safety & Standards
            </h2>
            <p className="text-textsecondary text-sm font-body">
              Pushpanjali operates as a registered cloud kitchen and follows applicable food safety and hygiene practices. Our kitchen operates under a valid <strong>FSSAI Registration/License</strong>. The FSSAI License Number will be displayed on our website and food packaging.
            </p>
          </section>

          {/* Section 4 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">4</span>
              Data Sharing
            </h2>
            <p className="text-textsecondary text-sm">We may share necessary information only with:</p>
            <ul className="list-disc list-inside text-sm text-textsecondary pl-4 space-y-1.5">
              <li>Delivery partners for cooking drop-off and order fulfillment</li>
              <li>Payment service providers for checkout processing</li>
              <li>Government authorities where legally required</li>
            </ul>
            <p className="text-sm font-semibold text-maroon mt-2">We never sell your personal information to third parties.</p>
          </section>

          {/* Section 5 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">5</span>
              Data Security
            </h2>
            <p className="text-textsecondary text-sm font-body">
              We use reasonable technical and organizational measures to protect your information. However, no internet transmission or storage method is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 6: Expanded Cookie Policy */}
          <section className="space-y-3 bg-maroon/5 p-6 rounded-2xl border border-maroon/10">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Cookie Policy
            </h2>
            <div className="text-textsecondary text-sm space-y-2 font-body">
              <p>
                Our website uses cookies to improve functionality, remember your preferences, and analyze website usage. Cookies are small text files stored on your local device.
              </p>
              <div className="space-y-1.5 pl-4 mt-2">
                <p className="font-semibold text-textprimary text-xs uppercase tracking-wider">Types of Cookies We Use:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Essential Cookies:</strong> Required to keep you signed in, protect checkout sessions, and secure payment transactions.</li>
                  <li><strong>Preference Cookies:</strong> Used to recognize you when you return and remember options like onboarding choices or cart state.</li>
                  <li><strong>Analytical Cookies:</strong> Help us measure visitor traffic, understand which dishes are popular, and optimize website speed.</li>
                </ul>
              </div>
              <p className="text-xs pt-1">
                You can choose to disable or block cookies through your browser preferences. However, please note that turning off essential cookies may disable key features of Pushpanjali, such as logging in or checking out.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">7</span>
              Your Rights
            </h2>
            <p className="text-textsecondary text-sm font-body">
              You may request access to, correction of, or deletion of your personal information by contacting us at our designated support channels.
            </p>
          </section>

          {/* Section 8 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">8</span>
              Service Area
            </h2>
            <p className="text-textsecondary text-sm flex items-center gap-1.5 font-body">
              <MapPin className="w-4 h-4 text-maroon shrink-0" />
              Currently, our delivery service is available only in selected areas of Noida, Uttar Pradesh, India.
            </p>
          </section>

          {/* Section 9 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">9</span>
              Policy Updates
            </h2>
            <p className="text-textsecondary text-sm font-body">
              We may update this Privacy Policy from time to time. Continued use of our website constitutes acceptance of the updated policy.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Footer Contact details */}
          <div className="bg-bgmain p-6 rounded-2xl border border-borderbeige space-y-3">
            <h3 className="font-heading font-bold text-lg text-textprimary">Contact Us</h3>
            <p className="text-sm text-textsecondary mt-1">For queries, requests, or feedback regarding your privacy, touch base with us at:</p>
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2 text-sm text-maroon font-semibold font-body">
                <Mail className="w-4 h-4" />
                <a href="mailto:contact@pushpanjali.cloud">contact@pushpanjali.cloud</a>
              </div>
            </div>
            <p className="text-xs text-textsecondary mt-4 font-body">
              *WhatsApp Support: Available through the support bubble displayed on our website pages.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
